import { UI } from '@skedulo/protobuf-web'
import { compareByRelevance } from '../../utils'
import { UiApiCrudClientBaseV1 } from '../crud-client-base'
import type {
  IGetDestinationsRequest,
  IGetDestinationsResponse,
  ILooseNavNode,
  INav,
  INavCreateRequest,
  INavDelRequest,
  INavGetManyRequest,
  INavGetRequest,
  INavNode,
  INavPutRequest,
} from '../../../types'

const BASE_PATH = '/v1/navs'

const prepareNavNodesForRest = (nodes?: ILooseNavNode[]): unknown[] =>
  nodes?.map(({ ref, destination, nodes, ...node }) => ({
    ...node,
    destination: UI.CRUD.destinationToJSON(destination),
    ref: destination === UI.CRUD.Destination.NOOP ? undefined : ref,
    nodes: prepareNavNodesForRest(nodes),
  })) ?? []

const validateNavNodes = (nodes?: UI.CRUD.NavNode[]): INavNode[] | undefined =>
  nodes?.map(({ label, destination, grantToRoleIds, ...node }): INavNode => {
    const nodes = validateNavNodes(node.nodes)
    // REST returns string destinations ("PLATFORM", "LINK", etc.) — normalize to enum
    destination = UI.CRUD.destinationFromJSON(destination)
    if (destination === UI.CRUD.Destination.DESTINATION_UNSPECIFIED) {
      destination = node.ref
        ? UI.CRUD.Destination.LINK
        : UI.CRUD.Destination.NOOP
    }
    if (!label || !destination) {
      throw Error('Invalid NavNode')
    }
    return { ...node, label, destination, grantToRoleIds, nodes } as INavNode
  })

export class NavUiApiClientV1 extends UiApiCrudClientBaseV1<INav> {
  create = async ({ nav: { nodes, ...nav } }: INavCreateRequest) => {
    const result = await this.restPost<UI.CRUD.Nav>(BASE_PATH, {
      ...nav,
      nodes: prepareNavNodesForRest(nodes),
    })
    return this.getItem(result)
  }

  get = async (request: INavGetRequest) =>
    this.getItem(await this.restGet<UI.CRUD.Nav>(`${BASE_PATH}/${request.uid}`))

  getUnrestricted = async (request: INavGetRequest) =>
    this.getItem(
      await this.restGet<UI.CRUD.Nav>(
        `${BASE_PATH}/unrestricted/${request.uid}`,
      ),
    )

  put = async ({ nav: { nodes, ...nav } }: INavPutRequest) => {
    const prepared = {
      ...nav,
      nodes: prepareNavNodesForRest(nodes),
    } as unknown as INav
    const result = await this.mergeAndPut(BASE_PATH, prepared)
    return this.getItem(result as unknown as UI.CRUD.Nav)
  }

  del = async (request: INavDelRequest) =>
    this.restDelete(`${BASE_PATH}/${request.uid}`)

  getMany = async (request: INavGetManyRequest) => {
    const { limit, page, name, ...rest } = request as Record<string, unknown>
    const queryParams = this.buildQueryParams({ limit, page, name, ...rest })
    const response = await this.restGet<{ items: INav[] }>(
      BASE_PATH,
      queryParams,
    )
    if (!response.items) throw new Error('Invalid response: missing "items"')
    return response.items.map((item) => this.getItem(item))
  }

  getBestNav = async (name: string): Promise<INav | null> =>
    (await this.getMany({ name, limit: 1000 } as INavGetManyRequest)).sort(
      compareByRelevance,
    )[0] || null

  getBestNavUnrestricted = async (name: string): Promise<INav | null> => {
    const queryParams = this.buildQueryParams({ name, limit: 1000 })
    const response = await this.restGet<{ items: INav[] }>(
      `${BASE_PATH}/unrestricted`,
      queryParams,
    )
    if (!response.items) throw new Error('Invalid response: missing "items"')
    return (
      response.items
        .map((item) => this.getItem(item))
        .sort(compareByRelevance)[0] || null
    )
  }

  public getItem(nav: UI.CRUD.Nav) {
    const { uid } = nav
    const nodes = validateNavNodes(nav.nodes)
    if (!uid || !nodes) {
      throw Error('Invalid response')
    }
    return { ...nav, uid, nodes }
  }

  public async getDestinations(
    request: IGetDestinationsRequest = {
      destination: UI.CRUD.Destination.DESTINATION_UNSPECIFIED,
    },
  ): Promise<IGetDestinationsResponse> {
    const queryParams: Record<string, string> = {}
    if (
      request.destination !== undefined &&
      request.destination !== UI.CRUD.Destination.DESTINATION_UNSPECIFIED
    ) {
      queryParams.destination = UI.CRUD.destinationToJSON(request.destination)
    }
    const response = await this.restGet<IGetDestinationsResponse>(
      '/v1/destinations',
      queryParams,
    )
    const nodes = validateNavNodes(response.nodes)
    if (!nodes) {
      throw Error('Invalid response')
    }
    return { ...response, nodes }
  }
}
